import clsx from 'clsx'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { BookingPrice, CurrencyEnum } from '../../../../../lib/graphql'
import { breakpoints, useMediaQuery } from '../../../../../lib/hooks'
import { decimalPrice, getPriceFromToggleStatus } from '../../../../../utils'
import { HTLabel, TTCLabel, VatLabel } from '../../../../atoms'
import {
  EnvironmentTypeEnum,
  useEnvironmentTypeContext,
  usePriceContext,
  UserTypeContextEnum,
  useUserTypeContext,
} from '../../../../contexts'

const TotalProposalContainer = ({
  price,
  isToggable,
  adults,
  currency,
}: {
  price: BookingPrice | undefined
  isToggable: boolean
  adults?: number
  currency?: CurrencyEnum
}) => {
  const toggleStatus = usePriceContext()
  const environmentType = useEnvironmentTypeContext()
  const isMobile = useMediaQuery(breakpoints.newMobile)
  const userContext = useUserTypeContext()

  const priceWithoutVat =
    userContext.userType === UserTypeContextEnum.USER
      ? decimalPrice(
          price?.totalUser?.priceWithDiscountAndFees?.priceWithoutVat,
          { currency },
        )
      : decimalPrice(price?.totalUser?.priceWithDiscount?.priceWithoutVat, {
          currency,
        })

  const priceWithVat =
    userContext.userType === UserTypeContextEnum.USER
      ? decimalPrice(price?.totalUser?.priceWithDiscountAndFees?.priceWithVat, {
          currency,
        })
      : decimalPrice(price?.totalUser?.priceWithDiscount?.priceWithVat, {
          currency,
        })

  const priceToDisplay = getPriceFromToggleStatus({
    priceOption: price?.totalUser ?? undefined,
    toggleStatus,
  })

  const pricePerPerson = adults ? priceToDisplay / adults : undefined

  const discountToDisplay = getPriceFromToggleStatus({
    priceOption: price?.totalUser ?? undefined,
    toggleStatus,
    discountPrices: true,
  })

  const discountPricePerPerson = adults ? discountToDisplay / adults : undefined

  const hasDiscount = priceToDisplay !== discountToDisplay

  return (
    <div className={s.container}>
      {isToggable ? (
        <>
          <div className={s.line}>
            <div className={s.totalBlock}>
              <p className={clsx(s.label, { [s.hasDiscount]: hasDiscount })}>
                <FormattedMessage
                  id='ui.TotalProposalContainer.totalStay'
                  defaultMessage='TOTAL SÉJOUR'
                />
              </p>
              <p className={clsx(s.amount, { [s.hasDiscount]: hasDiscount })}>
                {decimalPrice(priceToDisplay, { currency })} <VatLabel />
              </p>
            </div>
            <p
              className={clsx(s.labelPerPerson, {
                [s.hasDiscount]: hasDiscount,
              })}
            >
              {decimalPrice(pricePerPerson, { currency })} <VatLabel />
              <FormattedMessage
                id='ui.TotalProposalContainer.perTraveller'
                defaultMessage='/pers.'
              />
            </p>
          </div>

          {hasDiscount && !isMobile && (
            <div className={s.line}>
              <div className={s.totalBlock}>
                <p className={s.label}>
                  <FormattedMessage
                    id='ui.TotalProposalContainer.discountTotalStay'
                    defaultMessage='TOTAL SÉJOUR REMISÉ'
                  />
                </p>
                <p className={s.amount}>
                  {decimalPrice(discountToDisplay, { currency })} <VatLabel />
                </p>
              </div>

              <p className={clsx(s.labelPerPerson)}>
                {decimalPrice(discountPricePerPerson, { currency })}{' '}
                <VatLabel />
                /pers.
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className={s.line}>
            <p
              className={clsx(s.label, {
                [s.responsive]: environmentType === EnvironmentTypeEnum.APP,
              })}
            >
              <FormattedMessage
                id='ui.TotalProposalContainer.total'
                defaultMessage='TOTAL'
              />
            </p>
            <div className={s.amounts}>
              <p className={clsx(s.amount)}>
                {priceWithoutVat} <HTLabel className={s.greenLabel} />
              </p>
              <p className={clsx(s.amount)}>
                {priceWithVat} <TTCLabel className={s.greenLabel} />
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default TotalProposalContainer
